(() => {
  'use strict';

  const components = new Map();
  const initializedElements = new WeakMap();

  function register(name, selector, init) {
    components.set(name, { selector, init });
  }

  function initializeComponents(root = document) {
    components.forEach(({ selector, init }, name) => {
      const candidates = [];
      if (root instanceof Element && root.matches(selector)) candidates.push(root);
      candidates.push(...root.querySelectorAll?.(selector) || []);

      candidates.forEach(element => {
        const initialized = initializedElements.get(element) || new Set();
        if (initialized.has(name)) return;

        try {
          const cleanup = init(element);
          initialized.add(name);
          initializedElements.set(element, initialized);
          if (typeof cleanup === 'function') element[`__smibCleanup_${name}`] = cleanup;
        } catch (error) {
          console.error(`[SMIB] Component ${name} kon niet worden gestart.`, error);
        }
      });
    });
  }

  function updateHeaderLayout() {
    const header = document.querySelector('header-component');
    const headerGroup = document.querySelector('#header-group');
    if (!header || !headerGroup) return;

    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const headerComponent = document.querySelector('#header-component');
    if (headerComponent) headerComponent.dataset.menuStyle = mobile ? 'drawer' : 'menu';

    const topRow = header.querySelector('.header__row--top');
    const headerHeight = header.getBoundingClientRect().height || header.offsetHeight;
    let headerGroupHeight = 0;

    Array.from(headerGroup.children).forEach(child => {
      if (child === header || !(child instanceof HTMLElement)) return;
      headerGroupHeight += child.getBoundingClientRect().height || child.offsetHeight;
    });

    if (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
      headerGroupHeight += headerHeight;
    }

    document.body.style.setProperty('--header-height', `${headerHeight}px`);
    document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
    if (topRow) document.body.style.setProperty('--top-row-height', `${topRow.getBoundingClientRect().height || topRow.offsetHeight}px`);

    const hasHeaderSection = headerGroup.querySelector('.header-section');
    const hasImmediateSection = hasHeaderSection?.nextElementSibling?.classList.contains('shopify-section');
    const transparentOffset = hasHeaderSection && header.hasAttribute('transparent') && !hasImmediateSection ? '1' : '0';
    document.body.style.setProperty('--transparent-header-offset-boolean', transparentOffset);

    if (!mobile) {
      document.querySelectorAll('#Details-menu-drawer-container[open], .menu-drawer-container[open]').forEach(details => {
        details.removeAttribute('open');
        details.classList.remove('menu-open');
        details.querySelectorAll('[aria-expanded="true"]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      });

      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }
  }

  function initializeHeader() {
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateHeaderLayout);
    };

    schedule();
    const query = window.matchMedia('(max-width: 749px)');
    if (typeof query.addEventListener === 'function') query.addEventListener('change', schedule);
    else query.addListener(schedule);

    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });
    window.addEventListener('load', schedule, { once: true });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(schedule);
      const headerGroup = document.querySelector('#header-group');
      if (headerGroup) observer.observe(headerGroup);
    }
  }

  const PIPE_SKIP_SELECTOR = [
    'script', 'style', 'noscript', 'template', 'svg', 'textarea', 'select', 'option', 'input',
    'product-price', '.price', '.price-item', '.money', '[data-product-price]', '[data-price]',
    '.visually-hidden', '.smib-pipe-ignore', '.smib-pipe-mainline', '.smib-pipe-subline', '.smib-pipe-row'
  ].join(',');

  function shouldSkipPipe(textNode) {
    const parent = textNode.parentElement;
    return !parent || !textNode.nodeValue?.includes('|') || parent.closest(PIPE_SKIP_SELECTOR) || parent.dataset.smibPipeFormatted === 'true';
  }

  function formatPipeNode(textNode) {
    if (shouldSkipPipe(textNode)) return;
    const original = textNode.nodeValue;
    const pipeIndex = original.indexOf('|');
    const before = original.slice(0, pipeIndex).trim();
    const after = original.slice(pipeIndex + 1).trim();
    if (!before || !after) return;

    const parent = textNode.parentElement;
    parent.classList.add('smib-pipe-row');
    parent.dataset.smibPipeFormatted = 'true';

    const main = document.createElement('span');
    main.className = 'smib-pipe-mainline';
    main.textContent = before;
    const sub = document.createElement('span');
    sub.className = 'smib-pipe-subline';
    sub.textContent = after;
    textNode.replaceWith(main, sub);
  }

  function processPipes(root = document.body) {
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: node => shouldSkipPipe(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(formatPipeNode);
  }

  function updateExternalLinks(root = document) {
    root.querySelectorAll?.('a[href]').forEach(link => {
      const href = link.getAttribute('href');
      if (!href || /^(#|\/|mailto:|tel:|sms:|javascript:)/i.test(href)) return;
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) {
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
        }
      } catch (_error) {
        // Invalid links are left untouched.
      }
    });
  }

  function initCollectionTabs(section) {
    const buttons = Array.from(section.querySelectorAll('[data-line-button]'));
    const panels = Array.from(section.querySelectorAll('[data-line-panel]'));
    if (!buttons.length || !panels.length) return;

    const activate = (lineKey, moveFocus = false) => {
      buttons.forEach(button => {
        const active = button.dataset.lineButton === lineKey;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
        if (active && moveFocus) button.focus();
      });

      panels.forEach(panel => {
        const active = panel.dataset.linePanel === lineKey;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(button.dataset.lineButton));
      button.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = buttons.length - 1;
        activate(buttons[nextIndex].dataset.lineButton, true);
      });
    });
  }

  async function subscribeKlaviyo(form, profileAttributes, interests, options) {
    const globalConfig = window.SMIBSiteConfig?.klaviyo || {};
    if (globalConfig.enabled === false) throw new Error('SMIB Klaviyo formulieren zijn uitgeschakeld.');
    const companyId = String(options.companyId || globalConfig.companyId || '').trim();
    const listId = String(options.listId || globalConfig.defaultListId || '').trim();
    if (!companyId || !listId) throw new Error('Klaviyo Company ID of List ID ontbreekt.');

    const propertyName = String(options.propertyName || 'SMIB Interests').trim();
    const properties = { ...(profileAttributes.properties || {}) };
    properties[propertyName] = interests;

    const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: {
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
        revision: '2024-10-15',
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  ...profileAttributes,
                  properties,
                  subscriptions: {
                    email: { marketing: { consent: 'SUBSCRIBED' } },
                  },
                },
              },
            },
            custom_source: options.source || 'SMIB website',
          },
          relationships: {
            list: { data: { type: 'list', id: listId } },
          },
        },
      }),
    });

    if (!response.ok) throw new Error(`Klaviyo request failed (${response.status}).`);
    return response;
  }

  function initKlaviyoForm(form) {
    const message = form.querySelector('[data-smib-klaviyo-message]');
    const button = form.querySelector('button[type="submit"]');
    const emailInput = form.querySelector('input[name="email"]');
    if (!emailInput) return;
    const originalButtonText = button?.textContent.trim() || '';

    const setMessage = (text, state = '') => {
      if (!message) return;
      message.textContent = text;
      message.dataset.state = state;
      message.dataset.visible = text ? 'true' : 'false';
    };

    form.addEventListener('submit', async event => {
      event.preventDefault();
      setMessage('');
      if (!emailInput.checkValidity()) {
        emailInput.reportValidity();
        return;
      }

      const interests = Array.from(form.querySelectorAll('[data-smib-interest]:checked, input[name="smib_interests"]:checked, input[name="interests"]:checked'))
        .map(input => input.value)
        .filter(Boolean);

      if (form.dataset.requireInterest === 'true' && !interests.length) {
        setMessage(form.dataset.interestErrorMessage || 'Selecteer minimaal één onderwerp.', 'error');
        return;
      }

      if (button) {
        button.disabled = true;
        button.textContent = form.dataset.loadingMessage || 'Signing you up...';
      }

      const firstName = form.querySelector('[name="first_name"]')?.value.trim() || '';
      const lastName = form.querySelector('[name="last_name"]')?.value.trim() || '';
      const profile = { email: emailInput.value.trim().toLowerCase() };
      if (firstName) profile.first_name = firstName;
      if (lastName) profile.last_name = lastName;

      try {
        await subscribeKlaviyo(form, profile, interests, {
          companyId: form.dataset.companyId,
          listId: form.dataset.listId,
          propertyName: form.dataset.propertyName,
          source: form.dataset.source,
        });
        form.reset();
        setMessage(form.dataset.successMessage || 'Thank you for signing up.', 'success');
      } catch (error) {
        console.error('[SMIB Klaviyo]', error);
        setMessage(form.dataset.errorMessage || 'Something went wrong. Please try again.', 'error');
      } finally {
        if (button) {
          button.disabled = false;
          button.textContent = originalButtonText;
        }
      }
    });
  }

  function initHelpdesk(section) {
    if (section.dataset.singleOpen !== 'true') return;
    section.querySelectorAll('details').forEach(details => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        section.querySelectorAll('details[open]').forEach(other => {
          if (other !== details) other.open = false;
        });
      });
    });
  }

  register('collection-tabs', '[data-smib-component="collection-tabs"]', initCollectionTabs);
  register('klaviyo-form', '[data-smib-klaviyo-form]', initKlaviyoForm);
  register('helpdesk', '[data-smib-component="helpdesk"]', initHelpdesk);

  function start() {
    initializeHeader();
    processPipes(document.body);
    updateExternalLinks(document);
    initializeComponents(document);

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => mutation.addedNodes.forEach(node => {
        if (!(node instanceof Element)) return;
        processPipes(node);
        updateExternalLinks(node);
        initializeComponents(node);
      }));
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  document.addEventListener('shopify:section:load', event => {
    processPipes(event.target);
    updateExternalLinks(event.target);
    initializeComponents(event.target);
    requestAnimationFrame(updateHeaderLayout);
  });

  document.addEventListener('shopify:section:unload', event => {
    components.forEach((_component, name) => {
      const cleanup = event.target?.[`__smibCleanup_${name}`];
      if (typeof cleanup === 'function') cleanup();
    });
  });

  window.SMIB = Object.freeze({ register, initialize: initializeComponents, updateHeaderLayout });
})();
